import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { hotList } from '@/mocks/hotList';
import { useMemo } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import TrendBadge from '@/components/feature/TrendBadge';
import SubscribeForm from '@/components/feature/SubscribeForm';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => hotList.find((item) => String(item.id) === id), [id]);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = hotList.filter((item) => item.id !== article.id && item.category === article.category);
    const others = hotList.filter((item) => item.id !== article.id && item.category !== article.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const hotBoard = hotList.slice(0, 5);

  useSeo({
    title: article
      ? `${article.title} - 瓜圈速报`
      : '速报未找到 - 瓜圈速报',
    description: article
      ? `${article.summary} 瓜圈速报为你梳理事件背景、关键时间线与相关讨论。`
      : '这条速报可能已经下榜或链接有误，回到瓜圈速报查看当前热议焦点。',
    keywords: article
      ? `${article.category},${article.tags.join(',')},瓜圈速报,今日热议焦点`
      : '瓜圈速报,今日热议焦点',
    path: `/article/${id}`,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: article.summary,
            author: { '@type': 'Person', name: article.author },
            keywords: article.tags.join(','),
            articleSection: article.category,
            commentCount: article.comments,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://example.com/' },
              { '@type': 'ListItem', position: 2, name: '热度排行', item: 'https://example.com/ranking' },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <section className="mx-auto flex w-full max-w-[720px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
          <i className="ri-emotion-unhappy-line" />
        </span>
        <h1 className="mt-5 font-heading text-3xl tracking-wide text-foreground-950">这条速报已经下榜了</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          可能是链接有误，也可能是这条瓜已经过了热度峰值。回到榜单看看现在大家在围观什么。
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/ranking"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
          >
            <i className="ri-fire-line" />
            查看热度排行
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition hover:border-primary-300 hover:text-primary-600"
          >
            回到首页
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article className="pb-4">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '热度排行', to: '/ranking' },
              { label: article.category, to: '/topics' },
              { label: '速报详情' },
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.publishedAt}</span>
            <TrendBadge trend={article.trend} value={article.trendValue} />
          </div>

          <h1 className="mt-4 max-w-4xl text-balance font-heading text-3xl leading-tight tracking-wide text-foreground-950 md:text-[44px]">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-3-line text-foreground-400" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500" />
              热度 <span className="font-medium text-foreground-900">{article.heat}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-chat-3-line text-foreground-400" />
              {article.comments} 条讨论
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="h-56 w-full overflow-hidden rounded-card md:h-[380px]">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 瓜圈速报`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-[1.9] text-foreground-800 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-9 rounded-card border border-background-200 bg-background-100 p-5 md:p-7">
              <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                <i className="ri-history-line text-primary-500" />
                关键时间线
              </h2>
              <ol className="mt-6 space-y-6">
                {article.timeline.map((node, index) => (
                  <li key={index} className="relative flex gap-4 pb-1 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-primary-950">
                        {index + 1}
                      </span>
                      {index < article.timeline.length - 1 && (
                        <span className="mt-1 w-px flex-1 bg-background-300" />
                      )}
                    </div>
                    <div className="pb-1">
                      <p className="text-xs font-medium tracking-wide text-primary-600">{node.time}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{node.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs text-foreground-500">相关标签</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/ranking?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 rounded-card border border-accent-200 bg-accent-50 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6">
              <p className="text-sm leading-relaxed text-accent-900">
                <i className="ri-error-warning-line mr-1.5" />
                速报内容基于公开传播信息整理，涉及个人的描述不代表对其私人生活的判断，请理性吃瓜。
              </p>
              <Link
                to="/about"
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-900 underline underline-offset-4 transition hover:text-primary-600"
              >
                了解内容原则
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <aside className="flex flex-col gap-5 lg:col-span-4">
            <div className="rounded-card border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-fire-line text-lg text-primary-500" />
                实时热度榜
              </h2>
              <ol className="mt-4 space-y-3">
                {hotBoard.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/article/${item.id}`} className="group flex items-start gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index === 0 ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="shrink-0 font-heading text-sm tabular-nums text-foreground-400">
                        {item.heat}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-card border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">订阅瓜报</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                每天一封，只挑值得看的几条。
              </p>
              <div className="mt-4">
                <SubscribeForm />
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16">
          <div className="mb-7 flex items-end justify-between">
            <h2 className="font-heading text-2xl tracking-wide text-foreground-950">接着看这几条</h2>
            <Link
              to="/ranking"
              className="group inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition hover:text-primary-700"
            >
              更多速报
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>
    </article>
  );
}